import React, { useMemo } from "react";
import { Alert } from "@mui/material";
import CustomDataTable from "@components/DataTable/CustomDataTable";
import type { Header } from "@components/DataTable/CustomDataTable.types";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import { useBayHireOperationsQueue } from "@features/bay-hire/hooks/useBayHireOperationsQueue";
import type { BayHireQueueRow } from "@features/bay-hire/types";
import {
  bayHirePaymentTypeLabel,
  bayHirePreferenceLabel,
} from "@features/bay-hire/utils/bayHireLabels";
import { formatBayHireDateTime } from "@features/bay-hire/utils/formatBayHireDate";
import { getBayHireStatusBadgeType } from "@features/bay-hire/utils/statusBadge";
import { paths } from "@utils/navigation";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

/**
 * DFT-0015 manual Waiting List view (DFE-BAY-HIRE-003). Shows requests
 * Operations has manually deferred to WAITING_LIST, ordered by manual
 * priority then date added (via useBayHireQueuePaged's real offset
 * pagination).
 *
 * DFT-0027 (Slice B3): mirrors the Operations queue (Slice B2) - the per-row
 * lifecycle actions (Claim/Unclaim/Pre-approve/Reject/Cancel) that DFT-0034/
 * DFT-0015 placed on the row kebab have moved to the routed Request Detail
 * page (BayHireRequestDetail + BayHireFlowButtons). WL rows now navigate to
 * that detail route (canPreview + navigationLinkBase) where Operations users
 * act on the request; BayHireFlowButtons gates purely on task status
 * (READY/CLAIMED) + request status, so a waiting-listed request (which still
 * cycles through the same generic Operations task) is fully covered there.
 * This component is therefore a read-only, navigable queue.
 *
 * DFT-0027 (Slice E): this is now the "Waiting list" tab of
 * BayHireOperationsTabs - the page chrome (PageLayout + Subheader) and the old
 * "Back to Ongoing Queue" cross-navigation button moved to that container.
 * Per Figma p21 the column set is identical to the Ongoing tab's, so the
 * bespoke priority/dateAdded (and companyId/taskStatus/owner) columns were
 * dropped; the manual priority ordering itself is unchanged (backend-side).
 *
 * PCS does not infer bay availability or FCFS ordering here - priority is
 * a manual, Operations-entered value only (DFE-BAY-HIRE-003 non-goals).
 * Promotion out of WAITING_LIST reuses the existing DFT-0013 Pre-approve
 * dialog/action unchanged - WAITING_LIST -> PRE_APPROVED is already an
 * allowed domain transition.
 */
const BayHireWaitingListQueue: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    rows,
    isLoading,
    isError,
    page,
    pageSize,
    totalCount,
    onPageChange,
    filters,
    availableFields,
    handleApplyFilters,
    handleClearFilters,
  } = useBayHireOperationsQueue("WAITING_LIST");

  const tableRows = useMemo(
    () =>
      rows.map((row: BayHireQueueRow) => ({
        id: row.request.id,
        requestId: row.request.id,
        // FE-17/27: the visible "ID" column reads this, not `id` (a UUID kept only for row
        // navigation - canPreview reads `id` directly, unaffected by the header's own `field`).
        requestNo: row.request.requestNo ?? "-",
        numberOfBays: row.request.numberOfBays ?? "-",
        responsiblePersonName: row.request.responsiblePersonName,
        bayPreference: row.request.bayPreference,
        paymentType: row.request.paymentType,
        dateFrom: row.request.dateFrom,
        dateTo: row.request.dateTo,
        // DFT-0044 (FE-29): DD/MM/YYYY HH:MM, matching every other Marhub feature's own
        // date+time column (see formatBayHireDate.ts's formatBayHireDateTime).
        createdAt: formatBayHireDateTime(row.request.createdAt),
        status: row.request.status,
        badgeType: getBayHireStatusBadgeType(row.request.status),
        // DFT-0044 (FE-19): humanized display text, read by columns.tsx's status column.
        statusLabel: t(`bay-hire.status.${row.request.status}`, {
          defaultValue: row.request.status,
        }),
        // DFT-0044: humanized display text for the Request Bay/Payment type columns.
        bayPreferenceLabel: bayHirePreferenceLabel(t, row.request.bayPreference),
        paymentTypeLabel: bayHirePaymentTypeLabel(t, row.request.paymentType),
      })),
    [rows, t],
  );

  // DFT-0027 (Slice E2): same Figma column set as the Ongoing tab (p21).
  const headers: Header[] = useMemo(
    () => [
      // Megaplan (2026-08-05): explicit content-proportional `flex` on every header - identical
      // values to the vendor list (useBayHireRequestList.tsx) and BayHireOperationsQueue, since
      // all three share this 9-column shape.
      { field: "requestNo", columnName: t("bay-hire.table.id"), flex: 0.6 },
      // FE-27: baseline order (04_Port_License_details-2.png) puts the company responsible 2nd and
      // the bay count 3rd. Unchanged by batch 2.
      {
        field: "responsiblePersonName",
        columnName: t("bay-hire.table.responsiblePerson"),
        flex: 1.6,
      },
      {
        field: "numberOfBays",
        // Megaplan (2026-08-05): "Bay No" mislabeled the bay COUNT - renamed to "No of bays",
        // shared key with useBayHireRequestList.tsx's own column.
        columnName: t("bay-hire.table.bayNo"),
        flex: 0.6,
      },
      {
        field: "bayPreferenceLabel",
        columnName: t("bay-hire.table.requestBay"),
        flex: 0.9,
      },
      {
        field: "paymentTypeLabel",
        columnName: t("bay-hire.table.paymentType"),
        flex: 0.8,
      },
      {
        field: "dateFrom",
        columnName: t("bay-hire.table.dateFrom"),
        type: "date",
        flex: 0.8,
      },
      {
        field: "dateTo",
        columnName: t("bay-hire.table.dateTo"),
        type: "date",
        flex: 0.8,
      },
      {
        // DFT-0044 (FE-29): NOT type: "dateTime" - see the row mapper above.
        field: "createdAt",
        columnName: t("bay-hire.table.dateSubmitted"),
        flex: 1.0,
      },
      {
        field: "status",
        columnName: t("bay-hire.table.status"),
        flex: 0.9,
      },
    ],
    [t],
  );

  // DFT-0026: same LoadingOverlay + NoTableEntries migration as
  // BayHireOperationsQueue (see that component / plan.md Decision 1) -
  // this screen duplicates the same tri-state pattern.
  const queueContent = (
    <div style={{ position: "relative" }}>
      <LoadingOverlay loading={isLoading} testId="bay-hire-waiting-list-loading" />
      <CustomDataTable
        testId="bay-hire-waiting-list-table"
        title={t("bay-hire.waiting-list-title")}
        actionName=""
        headers={headers}
        rows={tableRows}
        page={page}
        pageSize={pageSize}
        pageSizeOptions={[10, 25, 50, 100]}
        totalCount={totalCount}
        onPageChange={onPageChange}
        // DFT-0027 (Slice E3): ⋮ menu with "View details" only - the lifecycle
        // actions stay on the detail header.
        actionColumn={{
          actionsDisplay: "menu",
          headerName: t("bay-hire.table.action"),
          actions: [
            {
              label: t("bay-hire.view-details"),
              testId: (row) => `bay-hire-waiting-list-view-details-${row.id}`,
              onClick: (row) => navigate(`/${paths.bayHire.requestDetailBase}/${row.id}`),
            },
          ],
        }}
        // DFT-0027 (Slice B3): a row click navigates to the routed detail page
        // where the Operations lifecycle actions now live (mirrors the
        // Operations queue, Slice B2). Absolute base since this queue is
        // mounted under /bay-hire/operations/waiting-list, and the detail
        // route is /bay-hire/request-details/:id. The row id === requestId.
        canPreview
        navigationLinkBase={`/${paths.bayHire.requestDetailBase}`}
        // DFT-0027 (Slice G): same Filters panel as the Ongoing tab (Figma
        // p21), minus the `status` field (this tab only ever holds
        // WAITING_LIST rows - see useBayHireOperationsQueue). hideFilters /
        // displaySubTitle={false} must stay off or CustomDataTable drops the
        // whole header block, Filters button included; hideTitle suppresses
        // only the duplicate heading (owned by BayHireOperationsTabs'
        // Subheader) while `title` still feeds NoTableEntries.
        filters={filters}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        availableFields={availableFields}
        hideTitle
      />
    </div>
  );

  // DFT-0027 (Slice E1): no PageLayout/Subheader here - BayHireOperationsTabs
  // owns the page chrome for both tabs.
  return (
    <div className="mt-8 px-24" data-testid="bay-hire-waiting-list">
      {isError && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="bay-hire-waiting-list-load-error">
          {t("bay-hire.waiting-list-load-error")}
        </Alert>
      )}

      {queueContent}
    </div>
  );
};

export default BayHireWaitingListQueue;
